import { CommandType, Monster } from '../types';

type CommandModifiers = Record<CommandType, number>;

const MODIFIER_SCALE = 0.01;
const MIN_MODIFIER = 0.5;
const MAX_MODIFIER = 2.0;

function clampModifier(value: number): number {
  return Math.max(MIN_MODIFIER, Math.min(MAX_MODIFIER, value));
}

/**
 * マッチアップ距離戦略モディファイア
 *
 * 自分と相手のステータスを比較し、有利な距離に戦いを誘導するための
 * コマンド重み調整を行う。
 *
 * - 武器攻撃力有利 → 近距離戦略（ADVANCE↑, WEAPON_ATTACK↑）
 * - 特殊攻撃力有利 → 遠距離戦略（RETREAT↑, SPECIAL_ATTACK↑）
 * - 相手のspecialが強く自分のリフレクターが少ない → 近距離に詰める
 *
 * @param self 自分のモンスター
 * @param opponent 相手モンスター
 * @returns コマンドごとの重みモディファイア
 */
export function getMatchupDistanceModifiers(
  self: Monster,
  opponent: Monster
): CommandModifiers {
  // 武器攻撃力の比較（strength × weapon multiplier）
  const selfWeaponPower = self.stats.strength * self.weapon.multiplier;
  const oppWeaponPower = opponent.stats.strength * opponent.weapon.multiplier;
  const weaponAdvantage = selfWeaponPower - oppWeaponPower;

  // 特殊攻撃力の比較
  const specialAdvantage = self.stats.special - opponent.stats.special;

  // 相手specialが強く自分のリフレクターが少ない場合、近距離に詰めたい
  const specialThreat = Math.max(0, opponent.stats.special - self.stats.special);
  const reflectorShortage = self.reflector.maxReflectCount <= 2 ? 1.0 : 0.5;
  const closeInFactor = specialThreat * reflectorShortage;

  return {
    [CommandType.ADVANCE]: clampModifier(
      1.0
      + MODIFIER_SCALE * weaponAdvantage
      + MODIFIER_SCALE * 0.8 * closeInFactor
      - MODIFIER_SCALE * 0.3 * Math.max(0, specialAdvantage)
    ),
    [CommandType.RETREAT]: clampModifier(
      1.0
      + MODIFIER_SCALE * Math.max(0, specialAdvantage)
      - MODIFIER_SCALE * 0.3 * Math.max(0, weaponAdvantage)
      - MODIFIER_SCALE * 0.3 * closeInFactor
    ),
    [CommandType.WEAPON_ATTACK]: clampModifier(
      1.0
      + MODIFIER_SCALE * Math.max(0, weaponAdvantage)
      + MODIFIER_SCALE * 0.4 * closeInFactor
    ),
    [CommandType.SPECIAL_ATTACK]: clampModifier(
      1.0
      + MODIFIER_SCALE * Math.max(0, specialAdvantage)
      - MODIFIER_SCALE * 0.3 * Math.max(0, -specialAdvantage)
    ),
    [CommandType.REFLECTOR]: clampModifier(
      1.0 + MODIFIER_SCALE * 0.5 * closeInFactor
    ),
    [CommandType.STANCE_A]: 1.0,
    [CommandType.STANCE_B]: 1.0,
  };
}
